import { Component, ChangeDetectionStrategy, signal, HostListener, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { MatSidenavModule } from '@angular/material/sidenav';
import { GlobalHeaderComponent } from '../../../../shared/components/layout/global-header/global-header';
import { GlobalFooterComponent } from '../../../../shared/components/layout/global-footer/global-footer';
import { GlobalSidebarComponent } from '../../../../shared/components/layout/global-sidebar/global-sidebar';
import { SidebarItem } from '../../../../shared/components/layout/global-sidebar/sidebar.model';
import { resolveMenuPermissionsFromRoute, routeChainHidesSidebar } from '../../../../shared/components/layout/global-sidebar/sidebar-utils';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../../core/services/breadcrumb.service';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { LocaleService } from '../../../../core/services/locale.service';

@Component({
  selector: 'app-cash-layout',
  standalone: true,
  imports: [
    GlobalHeaderComponent,
    GlobalFooterComponent,
    GlobalSidebarComponent,
    RouterOutlet,
    MatSidenavModule,
    BreadcrumbComponent,
    TranslatePipe,
  ],
  templateUrl: './cash-layout.component.html',
  styleUrls: ['./cash-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CashLayoutComponent implements OnInit {
  protected readonly breadcrumbService = inject(BreadcrumbService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private locale = inject(LocaleService);
  isMobile = signal(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  hideSidebar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => routeChainHidesSidebar(this.activatedRoute.snapshot)),
      startWith(routeChainHidesSidebar(this.activatedRoute.snapshot)),
    ),
    { requireSync: true },
  );

  menuConfig: SidebarItem[] = [
    { id: 'overview', label: 'cash.sidebar.overview', icon: 'grid_view', route: '/cash', exact: true },
    { id: 'bankAccount', label: 'cash.sidebar.bankAccount', icon: 'account_balance', route: '/cash/bank-account' },
    // { id: 'voucherManagement', label: 'cash.sidebar.voucherManagement', icon: 'receipt_long', route: '/cash/voucher-management' },
    { id: 'chequeOrderManagement', label: 'cash.sidebar.chequeOrderManagement', icon: 'payments', route: '/cash/cheque-order-management' },
    { id: 'ruleList', label: 'cash.sidebar.ruleList', icon: 'gavel', route: '/cash/rule-list' },
    // { id: 'cashbook', label: 'cash.sidebar.cashbook', icon: 'gavel', route: '/cash/cashbook' },
    // { id: 'bankBook', label: 'cash.sidebar.bankBook', icon: 'account_balance_wallet', route: '/cash/bank-book' },
    { id: 'cashBankBookRegister', label: 'cash.sidebar.cashBankBookRegister', icon: 'library_books', route: '/cash/cash-bank-book-register' },
    { id: 'mainLedger', label: 'cash.sidebar.mainLedger', icon: 'auto_stories', route: '/cash/main-ledger' },
    { id: 'ledgerRegister', label: 'cash.sidebar.ledgerRegister', icon: 'menu_book', route: '/cash/ledger-register' },
    { id: 'receiptManagement', label: 'cash.sidebar.receiptManagement', icon: 'point_of_sale', route: '/cash/receipt-management' },
    { id: 'openingBalance', label: 'cash.sidebar.openingBalance', icon: 'account_balance', route: '/cash/opening-balance' },
    { id: 'mandiSamitiIncome', label: 'cash.sidebar.mandiSamitiIncome', icon: 'pie_chart', route: '/cash/mandi-samiti-income' },
    { id: 'contraManagement', label: 'cash.sidebar.contraManagement', icon: 'sync_alt', route: '/cash/contra-management' },
    { id: 'debitVoucher', label: 'cash.sidebar.debitVoucher', icon: 'payments', route: '/cash/debit-voucher' },
    { id: 'fixedDeposit', label: 'cash.sidebar.fixedDeposit', icon: 'savings', route: '/cash/fixed-deposit' },
    // Dev-test screen only — see devOnlyGuard on its route. Dropped from the
    // menu outside local development so it never appears to real users.
    ...(this.locale.isDevelopment()
      ? [{ id: 'commonVoucherTest', label: 'cash.sidebar.commonVoucherTest', icon: 'bug_report', route: '/cash/common-voucher-test' }]
      : []),
  ];

  ngOnInit(): void {
    this.resolvePermissionsFromRouter();
  }

  private resolvePermissionsFromRouter(): void {
    const rootRoute = this.router.config.find(r => r.path === 'cash');
    this.menuConfig = resolveMenuPermissionsFromRoute(this.menuConfig, rootRoute, 'cash');
  }

  @HostListener('window:resize')
  onResize() {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth <= 768);
      if (this.isMobile()) this.sidenavOpened.set(false);
      else this.sidenavOpened.set(true);
    }
  }
  sidenavOpened = signal(typeof window !== 'undefined' ? window.innerWidth > 768 : true);

  toggleSidenav(): void {
    this.sidenavOpened.update(v => !v);
  }
}
